import React, { useState, useRef, useEffect, useCallback } from "react";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import Modal from "@mui/joy/Modal";
import ModalDialog from "@mui/joy/ModalDialog";
import styles from "./SearchBar.module.css";
import { Transition } from "react-transition-group";
import { extendTheme } from "@mui/joy/styles";
import { buyAndSellLink } from "../../config";
import { FaTimes, FaSearch } from "react-icons/fa";
import SelectHouseView from "../../Views/SelectHouseView/SelectHouseView.jsx";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { ToastContainer, toast } from "react-toastify";
import {
  TextField,
  Button,
  Box,
  Grid,
  FormControlLabel,
  Checkbox,
  Chip,
  Radio,
  RadioGroup,
} from "@mui/material";

export default function SearchBar() {
  const navigate = useNavigate();
  const { accessToken } = useAuth();

  const [placeholderText, setPlaceholderText] = useState("Type your address and select from dropdown");

  const [propertyData, setPropertyData] = useState({
    propertyType: "",
    bedrooms: "",
    bathrooms: "",
    areaSqft: "",
    streetAddress1: "",
    streetAddress2: "",
    landmark: "",
    city: "",
    state: "",
    zipcode: "",
    yearBuilt: "",
    images: null,
    heatingCooling: "",
  });

  const [filters, setFilters] = useState({
    priceMin: "",
    priceMax: "",
    beds: "",
    baths: "",
    type: [],
    zip: "",
    city: "",
    state: "",
    addressInput: "",
    keyword: "",
    professional: "",
  });

  const [searchModal, setSearchModal] = useState(false);
  const addressRef = useRef(null);

  const handleAddressChange = (e) => {
    const input = e.target.value;
    setFilters((prevFilters) => ({
      ...prevFilters,
      addressInput: input,
    }));
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      performSearch();
    }
  };

  const performSearch = () => {
    const { addressInput } = filters;
    const trimmedInput = addressInput?.trim() ?? "";

    const zipRegex = /^\d{5}(?:[-\s]\d{4})?$/;
    const stateRegex = /^[a-zA-Z]{2}$/;
    const unitRegex = /\b(apt|unit|suite|#|un)\s*\w+/i;

    let updatedFilters = {
      addressInput: trimmedInput,
      zip: "",
      city: "",
      state: "",
      unit: "",
      unitAddress: ""
    };

    if (!trimmedInput) {
      setFilters(updatedFilters);
      return;
    }

    // 🧠 STEP 1: If commas exist, split by comma
    let tokens = [];
    if (trimmedInput.includes(",")) {
      tokens = trimmedInput.split(",").map(p => p.trim()).filter(Boolean);
    } else {
      // No commas — treat as space-separated
      tokens = trimmedInput.split(/\s+/);
    }

    // 🧠 STEP 2: Work backwards to identify known suffixes
    let zip = "";
    let state = "";
    let city = "";
    let unit = "";
    let streetParts = [];

    for (let i = tokens.length - 1; i >= 0; i--) {
      const token = tokens[i];

      if (!zip && zipRegex.test(token)) {
        zip = token;
      } else if (!state && stateRegex.test(token) && token.length === 2) {
        state = token.toUpperCase();
      } else if (!city && /^[a-zA-Z\s]+$/.test(token)) {
        city = token;
      } else {
        streetParts.unshift(token); // build from front
      }
    }

    // 🧠 STEP 3: Match unit anywhere in full address string
    const unitMatch = trimmedInput.match(unitRegex);
    if (unitMatch) {
      unit = unitMatch[0];
    }

    // 🧠 STEP 4: Remove unit from street to clean `unitAddress`
    const streetFull = streetParts.join(" ");
    const cleanedStreet = unit ? streetFull.replace(unit, "").trim() : streetFull;

    // ✅ Final assignment
    updatedFilters.unit = unit;
    updatedFilters.city = city;
    updatedFilters.state = state;
    updatedFilters.zip = zip;
    updatedFilters.unitAddress = cleanedStreet;

    setFilters(updatedFilters);

    navigate("/SelectHouse", {
      state: {
        fullAddress: updatedFilters.addressInput,
        zip: updatedFilters.zip,
        city: updatedFilters.city,
        state: updatedFilters.state
      }
    });
  };



  const showErrorMessage = (text) => {
    toast.error(text, {
      position: "top-center",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      progress: undefined,
      theme: "colored",
    });
  };

  const openSearchModal = () => setSearchModal(true);
  const closeSearchModal = () => setSearchModal(false);

  const theme = extendTheme({
    components: {
      ModalDialog: {
        defaultProps: { layout: "top" },
        styleOverrides: {
          root: ({ ownerState }) => ({
            ...(ownerState.layout === "top" && {
              top: "12vh",
              left: "50%",
              transform: "translateX(-50%)",
            }),
          }),
        },
      },
    },
  });

  const handlePlaceSelect = useCallback((place) => {
    let streetNumber = "", route = "", city = "", state = "", zipcode = "", unit = "", formattedAddress = "";

    try {
      if (!place || !place.geometry) {
        showErrorMessage("Please select a valid address from the dropdown");
        return;
      }

      formattedAddress = place.formatted_address;
      const unitMatch = formattedAddress.match(/(Apt|Unit|Suite|#)\s?([A-Za-z0-9\-]+)/i);
      if (unitMatch) unit = `${unitMatch[1]} ${unitMatch[2]}`;

      if (Array.isArray(place.address_components)) {
        for (const component of place.address_components) {
          const type = component.types[0];
          switch (type) {
            case "street_number": streetNumber = component.long_name || ""; break;
            case "route": route = component.long_name || ""; break;
            case "locality": city = component.long_name || ""; break;
            case "administrative_area_level_1": state = component.short_name || ""; break;
            case "postal_code": zipcode = component.long_name || ""; break;
          }
        }
      }

      setPropertyData((prev) => ({
        ...prev,
        streetAddress1: `${streetNumber} ${route}`.trim(),
        streetAddress2: unit,
        city,
        state,
        zipcode,
      }));

      const unitAddress = `${streetNumber} ${route}`.trim();
      navigate("/SelectHouse", {
        state: {
          formattedAddress,
          unitAddress,
          unit,
          zipcode,
          city,
          state,
        },
      });
    } catch (error) {
      console.error("Error in handlePlaceSelect:", error);
      showErrorMessage("There was an error processing the address");
    }
  }, []);

  useEffect(() => {
    let timeoutId;
    let autocompleteInstance = null;

    const initPlacesAutocomplete = () => {
      try {
        const input = document.getElementById("googleAutocomplete");
        if (!input) return;

        autocompleteInstance = new window.google.maps.places.Autocomplete(input, {
          types: ["address"],
          componentRestrictions: { country: "us" },
          fields: ["address_components", "geometry", "formatted_address"],
        });

        autocompleteInstance.addListener("place_changed", () => {
          const place = autocompleteInstance.getPlace();
          handlePlaceSelect(place);
        });
      } catch (error) {
        console.error("Error initializing Places Autocomplete:", error);
      }
    };

    timeoutId = setTimeout(initPlacesAutocomplete, 500);

    return () => {
      clearTimeout(timeoutId);
      if (autocompleteInstance) {
        window.google?.maps?.event?.clearInstanceListeners(autocompleteInstance);
      }
    };
  }, [handlePlaceSelect]);

  return (
    <>
      <Transition in={searchModal} timeout={400}>
        {(state) => (
          <Modal
            open={!["exited", "exiting"].includes(state)}
            onClose={closeSearchModal}
            slotProps={{
              backdrop: {
                sx: {
                  opacity: 0,
                  backdropFilter: "none",
                  transition: `opacity 400ms, backdrop-filter 400ms`,
                  ...{
                    entering: { opacity: 1, backdropFilter: "blur(8px)" },
                    entered: { opacity: 1, backdropFilter: "blur(8px)" },
                  }[state],
                },
              },
            }}
            sx={{ visibility: state === "exited" ? "hidden" : "visible" }}
          >
            <ModalDialog
              aria-labelledby="fade-modal-dialog-title"
              aria-describedby="fade-modal-dialog-description"
              theme={theme}
              sx={{
                maxHeight: "85vh",
                overflow: "auto",
                opacity: 0,
                transition: `opacity 300ms`,
                ...{
                  entering: { opacity: 1 },
                  entered: { opacity: 1 },
                }[state],
              }}
            >
              <div className={styles.modalContainer}>
                <div className={styles.searchBox}></div>
                <div className={styles.searchTiles}>
                  <a href={buyAndSellLink}>Buy & Sell</a>
                  <a href="/Rent-a-Property">Rent a Property</a>
                  <a href="/Realtors-and-Brokers">Realtors and Brokers</a>
                  <a href="/Education">Education</a>
                </div>
              </div>
            </ModalDialog>
          </Modal>
        )}
      </Transition>

      <Grid item key="address-search">
        <div className={styles.googleAddressContainer}>
          <input
            ref={addressRef}
            id="googleAutocomplete"
            className={styles.googleAutocomplete}
            placeholder="Type the address and select from dropdown"
            type="text"
            value={filters.addressInput}
            onChange={handleAddressChange}
            onKeyDown={handleKeyDown}
            onFocus={() => setPlaceholderText("")}
          />
        </div>
      </Grid>
    </>
  );
}
